import React from 'react';
import {Box, Button, Chip, CircularProgress, ListItem, Stack, Typography} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import CancelIcon from '@mui/icons-material/Cancel';
import {useReportStore} from '../../../../features/products/useReportStore';
import useSourcesExplorerStore from '../../../../features/products/reportEditorPage/leftPanel/components/useSourcesExplorerStore';
import {normalizeKeywordsQuery} from './utils/normalizeKeywordsQuery';
import {getClaimSuggestedCount, getClaimSupportingCount} from './utils/getClaimBadgeCounts';
import {
    getContradictionCountForClaim,
    hasExtrinsicCorrection,
} from './utils/claimProofAlerts';

const BADGE_SX = {
    height: 22,
    fontSize: '0.68rem',
    fontWeight: 600,
    flexShrink: 0,
    '& .MuiChip-label': {
        px: 0.75,
        whiteSpace: 'nowrap',
    },
};

/** Min width fits "99 supporting" in the margin rail */
const SUGGESTED_BADGE_MIN_WIDTH = 82;
const SUPPORTING_BADGE_MIN_WIDTH = 96;

/**
 * Individual claim item component
 * Displays a single claim with status, importance, evidence indicators, and search action
 *
 * @param {Object} props
 * @param {Object} props.claim - The claim object
 * @param {number} props.claim.id - Claim ID
 * @param {string} props.claim.claim - Claim text
 * @param {string} props.claim.importance - 'high' | 'medium' | 'low'
 * @param {string} props.claim.status - 'proven' | 'partially_proven' | 'not_proven' | 'contradicted'
 * @param {Array<number>} props.claim.supporting_evidence - Array of evidence indices
 * @param {Array<number>} props.claim.contradicting_evidence - Array of evidence indices
 * @param {string} props.claim.keywords_query - Search query for this claim
 * @param {string} props.claim.boolean_query - boolean Search query for this claim
 * @param {Array} props.references - Added references for the mark
 * @param {Array} [props.suggestedReferences] - V4 backend suggestions
 * @param {object[]} [props.contradictions] - V4 contradiction groups
 * @param {Record<string, string>} [props.extrinsicNotes] - V4 extrinsic notes by claimId
 * @param {boolean} [props.insightsLoading] - V4 suggest pipeline still running
 * @param {Function} props.onClaimClick - Callback when claim is clicked to view details
 */
export default function ClaimItem({
    claim,
    references = [],
    suggestedReferences = [],
    contradictions = [],
    extrinsicNotes = {},
    insightsLoading = false,
    onClaimClick,
}) {
    const setLeftPanelTab = useReportStore(state => state.setLeftPanelTab);
    const setUnifiedSearchMode = useReportStore(state => state.setUnifiedSearchMode);
    const setInputValue = useSourcesExplorerStore(state => state.setInputValue);

    const handleSearchExamples = React.useCallback((e) => {
        // Stop propagation to prevent claim dialog from opening
        e.stopPropagation();

        // Prefer boolean keywords_query; fall back to semantic_query or plain claim text
        const booleanQuery = normalizeKeywordsQuery(claim.keywords_query);
        const semanticQuery = claim.semantic_query?.trim() || claim.claim?.trim();

        if (booleanQuery) {
            setInputValue(booleanQuery);
            setUnifiedSearchMode('keyword');
        } else if (semanticQuery) {
            setInputValue(semanticQuery);
            setUnifiedSearchMode('ai');
        } else {
            return;
        }

        setLeftPanelTab('search');
    }, [claim.keywords_query, claim.semantic_query, claim.claim, setUnifiedSearchMode, setLeftPanelTab, setInputValue]);

    const handleClaimClick = React.useCallback(() => {
        onClaimClick?.(claim);
    }, [claim, onClaimClick]);

    // Get status icon, label, and color
    const getStatusDisplay = () => {
        switch (claim.status) {
            case 'proven':
                return {
                    icon: <CheckCircleIcon sx={{fontSize: 16, color: '#2e7d32'}}/>,
                    label: 'Proven',
                    color: '#2e7d32'
                };
            case 'partially_proven':
                return {
                    icon: <CheckCircleOutlineIcon sx={{fontSize: 16, color: '#ed6c02'}}/>,
                    label: 'Partially Proven',
                    color: '#ed6c02'
                };
            case 'contradicted':
                return {
                    icon: <CancelIcon sx={{fontSize: 16, color: '#d32f2f'}}/>,
                    label: 'Contradicted',
                    color: '#d32f2f'
                };
            case 'not_proven':
            default:
                return {
                    icon: <HelpOutlineIcon sx={{fontSize: 16, color: '#ed6c02'}}/>,
                    label: 'Not Proven',
                    color: '#ed6c02'
                };
        }
    };

    const statusDisplay = getStatusDisplay();
    const suggestedCount = getClaimSuggestedCount(claim, references, suggestedReferences);
    const supportingCount = getClaimSupportingCount(claim, references);
    const contradictionCount = getContradictionCountForClaim(contradictions, claim.id);
    const hasCorrection = hasExtrinsicCorrection(extrinsicNotes, claim.id);

    return (
        <ListItem
            onClick={handleClaimClick}
            data-testid={`claim-item-${claim.id}`}
            sx={{
                bgcolor: 'rgba(0,0,0,0.02)',
                p: 2,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'stretch',
                gap: 1,
                cursor: 'pointer',
                '&:hover': {
                    bgcolor: 'rgba(0,0,0,0.06)',
                },
            }}
        >
            {/* Row 1: Status and Importance */}
            <Box sx={{display: 'flex', alignItems: 'center', gap: 1}}>
                {/* Status indicator */}
                <Box sx={{display: 'flex', alignItems: 'center', gap: 0.5}}>
                    {statusDisplay.icon}
                    <Typography variant="body2" sx={{color: statusDisplay.color, fontWeight: 500, fontSize: '0.8rem'}}>
                        {statusDisplay.label}
                    </Typography>
                </Box>

                {/* Importance badge */}
                {/*<Chip*/}
                {/*    label={claim.importance}*/}
                {/*    size="small"*/}
                {/*    sx={{*/}
                {/*        bgcolor: getImportanceColor(),*/}
                {/*        color: 'white',*/}
                {/*        height: 20,*/}
                {/*        textTransform: 'capitalize',*/}
                {/*        ml: 'auto',*/}
                {/*    }}*/}
                {/*/>*/}
            </Box>

            {/* Row 2: Claim text */}
            <Typography variant="body2" sx={{fontWeight: 500, fontSize: '0.875rem', lineHeight: 1.4}}>
                {claim.claim}
            </Typography>

            {/* Row 3: Search + evidence badges — stacked to avoid cramped wrap in narrow rail */}
            <Stack spacing={0.75} sx={{width: '100%'}}>
                <Button
                    size="small"
                    startIcon={<SearchIcon sx={{fontSize: 18}}/>}
                    onClick={handleSearchExamples}
                    data-testid={`claim-item-search-${claim.id}`}
                    sx={{
                        alignSelf: 'flex-start',
                        textTransform: 'none',
                        whiteSpace: 'nowrap',
                        flexShrink: 0,
                        minWidth: 'unset',
                        py: 0.25,
                        px: 1,
                    }}
                >
                    Search
                </Button>

                <Stack
                    direction="row"
                    spacing={0.5}
                    sx={{
                        alignSelf: 'flex-end',
                        flexWrap: 'nowrap',
                        maxWidth: '100%',
                    }}
                >
                    {insightsLoading ? (
                        <Box sx={{display: 'flex', alignItems: 'center', gap: 0.5}}>
                            <CircularProgress size={12} thickness={4} />
                            <Typography variant="caption" sx={{color: 'text.secondary', fontSize: '0.68rem'}}>
                                suggested
                            </Typography>
                        </Box>
                    ) : (
                        <Chip
                            label={`${suggestedCount} suggested`}
                            size="small"
                            variant="outlined"
                            data-testid={`claim-item-suggested-${claim.id}`}
                            sx={{
                                ...BADGE_SX,
                                minWidth: SUGGESTED_BADGE_MIN_WIDTH,
                                borderColor: '#ed6c02',
                                color: '#ed6c02',
                                bgcolor: 'rgba(237, 108, 2, 0.08)',
                            }}
                        />
                    )}
                    <Chip
                        label={`${supportingCount} supporting`}
                        size="small"
                        variant="outlined"
                        data-testid={`claim-item-supporting-${claim.id}`}
                        sx={{
                            ...BADGE_SX,
                            minWidth: SUPPORTING_BADGE_MIN_WIDTH,
                            borderColor: '#2e7d32',
                            color: '#2e7d32',
                            bgcolor: 'rgba(46, 125, 50, 0.08)',
                        }}
                    />
                </Stack>

                {(insightsLoading || contradictionCount > 0 || hasCorrection) && (
                    <Stack
                        direction="row"
                        spacing={0.5}
                        alignItems="center"
                        sx={{
                            alignSelf: 'flex-end',
                            flexWrap: 'wrap',
                            maxWidth: '100%',
                        }}
                    >
                        {insightsLoading ? (
                            <>
                                <CircularProgress size={12} thickness={4} />
                                <Typography variant="caption" sx={{color: 'text.secondary', fontSize: '0.68rem'}}>
                                    checking contradictions and corrections...
                                </Typography>
                            </>
                        ) : (
                            <>
                                {contradictionCount > 0 && (
                                    <Chip
                                        label={`${contradictionCount} contradiction${contradictionCount === 1 ? '' : 's'}`}
                                        size="small"
                                        variant="outlined"
                                        data-testid={`claim-item-contradiction-${claim.id}`}
                                        sx={{
                                            ...BADGE_SX,
                                            borderColor: '#d32f2f',
                                            color: '#d32f2f',
                                            bgcolor: 'rgba(211, 47, 47, 0.08)',
                                        }}
                                    />
                                )}
                                {hasCorrection && (
                                    <Chip
                                        label="1 correction"
                                        size="small"
                                        variant="outlined"
                                        data-testid={`claim-item-correction-${claim.id}`}
                                        sx={{
                                            ...BADGE_SX,
                                            borderColor: '#ed6c02',
                                            color: '#ed6c02',
                                            bgcolor: 'rgba(237, 108, 2, 0.1)',
                                        }}
                                    />
                                )}
                            </>
                        )}
                    </Stack>
                )}
            </Stack>
        </ListItem>
    );
}
